import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { childApi, staffApi, getToken, setToken, clearAllTokens } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = loading

  // Sessions are carried by httpOnly cookies; in-memory tokens are a same-tab
  // fallback. On load we simply ask the backend who we are.
  const refresh = useCallback(async () => {
    try {
      const { data } = await childApi.get("/auth/me");
      if (data.kind === "chorister") {
        setSession({ kind: "chorister", profile: data.chorister });
        return;
      }
    } catch { /* not a chorister session */ }
    try {
      const { data } = await staffApi.get("/auth/me");
      if (data.kind === "staff") {
        setSession({ kind: "staff", profile: data.user });
        return;
      }
    } catch { /* not a staff session */ }
    setSession(null);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loginChorister = async (payload) => {
    clearAllTokens();
    const { data } = await childApi.post("/auth/chorister/login", payload);
    setToken("child", data.token);
    setSession({ kind: "chorister", profile: data.chorister });
    return data;
  };

  const loginStaff = async (payload) => {
    clearAllTokens();
    const { data } = await staffApi.post("/auth/staff/login", payload);
    setToken("staff", data.token);
    setSession({ kind: "staff", profile: data.user });
    return data;
  };

  const logout = useCallback(() => {
    staffApi.post("/auth/logout").catch(() => {}); // clears both httpOnly cookies
    clearAllTokens();
    sessionStorage.clear();
    setSession(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ session, loginChorister, loginStaff, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
